import React from 'react';
import { useLanguage } from '../LanguageContext';
import type { ProjectTimeline as ProjectTimelineData } from '../../hooks/useDLDData';
import { appLocale } from '../../i18n/locale';

/**
 * Milestone labels arrive from the DLD backend in English. Map the known set to
 * dictionary keys for display; anything unrecognised falls back to the raw label
 * so a new backend milestone is never swallowed.
 */
const MILESTONE_KEYS: Record<string, string> = {
  'Booking Started': 'adMilestoneBookingStarted',
  'Construction Started': 'adMilestoneConstructionStarted',
  'Construction Progress': 'adMilestoneConstructionProgress',
  'Expected Completion': 'adMilestoneExpectedCompletion',
};

interface ProjectTimelineProps {
  data: ProjectTimelineData | null;
  loading: boolean;
  error: string | null;
  language?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '-';
    return d.toLocaleDateString(appLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
}

export default function ProjectTimeline({ data, loading, error, language }: ProjectTimelineProps) {
  const { t } = useLanguage();

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#e5e7eb', animation: 'pulse 1.5s infinite' }} />
          <div style={{ width: 140, height: 16, borderRadius: 4, background: '#e5e7eb', animation: 'pulse 1.5s infinite' }} />
        </div>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e5e7eb', animation: 'pulse 1.5s infinite' }} />
            <div style={{ flex: 1, height: 32, borderRadius: 8, background: '#f3f4f6', animation: 'pulse 1.5s infinite' }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #fecaca' }}>
        <p style={{ color: '#dc2626', fontSize: 13 }}>{t('adTimelineLoadFailed')}: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>{t('adProjectTimeline')}</h3>
        <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>
          {t('adTimelineUnavailable')}
        </p>
      </div>
    );
  }

  const completionPct = data.completionPercentage || 0;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 4px' }}>
          {t('adProjectTimeline')}
        </h3>
        {data.developerName && (
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {t('developer')}: {data.developerName}
          </div>
        )}
        {data.status && (
          <span style={{
            display: 'inline-block', marginTop: 6,
            padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
            backgroundColor: data.status.toLowerCase().includes('active') ? '#dcfce7' : '#fef9c3',
            color: data.status.toLowerCase().includes('active') ? '#16a34a' : '#ca8a04',
          }}>
            {data.status}
          </span>
        )}
      </div>

      {/* Vertical Timeline */}
      <div style={{ position: 'relative', paddingLeft: 32 }}>
        {/* Vertical line */}
        <div style={{
          position: 'absolute', left: 11, top: 4, bottom: 4, width: 2,
          background: 'linear-gradient(to bottom, #0d9488, #e5e7eb)',
        }} />

        {data.milestones.map((milestone, i) => {
          const isProgress = milestone.label === 'Construction Progress';

          return (
            <div key={i} style={{ position: 'relative', marginBottom: i < data.milestones.length - 1 ? 28 : 0 }}>
              {/* Node */}
              <div style={{
                position: 'absolute', left: -32, top: 0,
                width: 24, height: 24, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: milestone.completed ? '#0d9488' : '#fff',
                border: milestone.completed ? 'none' : '2px solid #d1d5db',
                zIndex: 1,
              }}>
                {milestone.completed ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#d1d5db' }} />
                )}
              </div>

              {/* Content */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: milestone.completed ? '#111' : '#6b7280' }}>
                  {MILESTONE_KEYS[milestone.label] ? t(MILESTONE_KEYS[milestone.label]) : milestone.label}
                </div>
                {milestone.date && !isProgress && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {formatDate(milestone.date)}
                  </div>
                )}

                {/* Progress bar for construction */}
                {isProgress && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0d9488' }}>
                        {completionPct.toFixed(0)}%
                      </span>
                      <span style={{ fontSize: 11, color: '#6b7280' }}>{t('adComplete')}</span>
                    </div>
                    <div style={{
                      width: '100%', height: 10, backgroundColor: '#e5e7eb', borderRadius: 5, overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${Math.min(completionPct, 100)}%`, height: '100%',
                        background: completionPct >= 100
                          ? 'linear-gradient(90deg, #0d9488, #10b981)'
                          : 'linear-gradient(90deg, #0d9488, #14b8a6)',
                        borderRadius: 5,
                        transition: 'width 0.6s ease-in-out',
                      }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Project Details */}
      <div style={{ marginTop: 24, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          {data.projectNumber && (
            <div>
              <span style={{ color: '#6b7280' }}>{t('adProjectNo')}: </span>
              <span style={{ color: '#111', fontWeight: 600 }}>{data.projectNumber}</span>
            </div>
          )}
          {data.totalUnits && (
            <div>
              <span style={{ color: '#6b7280' }}>{t('totalUnits')}: </span>
              <span style={{ color: '#111', fontWeight: 600 }}>{data.totalUnits.toLocaleString()}</span>
            </div>
          )}
          {data.totalArea && (
            <div>
              <span style={{ color: '#6b7280' }}>{t('adTotalArea')}: </span>
              <span style={{ color: '#111', fontWeight: 600 }}>{parseFloat(String(data.totalArea)).toLocaleString()} m²</span>
            </div>
          )}
          {data.escrowAccount && (
            <div>
              <span style={{ color: '#6b7280' }}>{t('adEscrow')}: </span>
              <span style={{ color: '#111', fontWeight: 600 }}>{data.escrowAccount}</span>
            </div>
          )}
        </div>
      </div>

      {/* Source */}
      <div style={{ marginTop: 16, fontSize: 10, color: '#9ca3af', borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
        {t('adSourceMashrooi')} {data.projectName}
      </div>
    </div>
  );
}
